import React from 'react'
import Typography from '@mui/material/Typography'
import { CheckCircle } from '@mui/icons-material'
import TextInput from 'components/molecules/TextInput/TextInput'
import { LoadingButton } from 'components/mui'
import { PatientWalletCardSenderProps } from './PatientWalletCardSender.types'
import { usePatientWalletCardSenderLogic } from './PatientWalletCardSender.logic'
import { useIsMobile } from 'lib/utils/useIsMobile'
import styles from './PatientWalletCardSender.module.scss'
import classNames from 'classnames'

export const PatientWalletCardSender = (
  props: PatientWalletCardSenderProps
) => {
  const {
    formData,
    errors,
    isLoading,
    isSuccess,
    handleInputChange,
    handleSubmit,
  } = usePatientWalletCardSenderLogic(props)

  const isMobile = useIsMobile()

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSubmit()
  }

  return (
    <div className={styles.patientWalletCardSenderContainer}>
      <div className={styles.walletCardHeader}>
        <div className={styles.walletHeaderTitle}>
          <Typography variant="h3" sx={{ transform: 'none' }}>
            Send Patient Wallet Card
          </Typography>
          <Typography variant="body2" className={styles.secondaryText}>
            Share digital wallet card with patient via email or SMS
          </Typography>
        </div>
      </div>

      <form onSubmit={handleFormSubmit} className={styles.walletCardForm}>
        <div
          className={classNames(styles.nameInputsRow, {
            [styles.isMobile]: isMobile,
          })}
        >
          <div className={styles.firstNameInput}>
            <TextInput
              id="firstName"
              label="First Name"
              name="firstName"
              variant="outlined"
              value={formData.firstName}
              handleChangeFormData={(e) =>
                handleInputChange('firstName', e.target.value)
              }
              fullWidth
              error={!!errors.firstName}
              errorMessage={errors.firstName}
              disabled={isLoading}
            />
          </div>

          <div className={styles.lastNameInput}>
            <TextInput
              id="lastName"
              label="Last Name"
              name="lastName"
              variant="outlined"
              value={formData.lastName}
              handleChangeFormData={(e) =>
                handleInputChange('lastName', e.target.value)
              }
              fullWidth
              error={!!errors.lastName}
              errorMessage={errors.lastName}
              disabled={isLoading}
            />
          </div>
        </div>

        <div
          className={classNames(styles.contactInputsRow, {
            [styles.isMobile]: isMobile,
          })}
        >
          <div className={styles.emailInput}>
            <TextInput
              id="email"
              label="Email Address"
              name="email"
              variant="outlined"
              value={formData.email}
              handleChangeFormData={(e) =>
                handleInputChange('email', e.target.value)
              }
              fullWidth
              error={!!errors.email}
              errorMessage={errors.email}
              disabled={isLoading}
              validateEmail
            />
          </div>

          <div className={styles.phoneInput}>
            <TextInput
              id="phoneNumber"
              label="Phone Number"
              name="phoneNumber"
              variant="outlined"
              value={formData.phoneNumber}
              handleChangeFormData={(e) =>
                handleInputChange('phoneNumber', e.target.value)
              }
              fullWidth
              error={!!errors.phoneNumber}
              errorMessage={errors.phoneNumber}
              disabled={isLoading}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        <LoadingButton
          type="submit"
          loading={isLoading}
          disabled={isLoading || isSuccess}
          className={styles.submitButton}
        >
          {isLoading ? 'Sending...' : isSuccess ? 'Sent!' : 'Send Wallet Card'}
        </LoadingButton>

        {isSuccess && (
          <div className={styles.successMessage}>
            <CheckCircle className={styles.successIcon} />
            <Typography variant="body2" className={styles.successText}>
              Patient wallet card sent successfully!
            </Typography>
          </div>
        )}
      </form>
    </div>
  )
}
